import { Subscribe } from '@react-rxjs/core'
import React, { useMemo } from 'react'
import styled, { css } from 'styled-components'

import { ConfigurableProductDetailsFragment } from '../../../graphql/magento'
import { useResultWithLeftEffect } from '../../../hooks/useResultWithLeftEffect'
import {
  findVariantInProductQuery,
  useQueryProductByUrlKeyVaryByAuth,
} from '../../../lib/products/m2-products-by-url-key'
import { reduceSet } from '../../../utils/collectionTools'
import { constNull } from '../../../utils/constant'
import { formatAsCurrency } from '../../../utils/priceTools'
import { pluckData } from '../../../utils/result'
import { StyledFunctionComponent } from '../../types'
import { TypographyShimmer } from '../../TypographyShimmer'

export interface ItemSelectorOptionPriceProps {
  item: ConfigurableProductDetailsFragment
  skus: ReadonlyArray<string>
}

const priceLabelStyles = css`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
`

const PriceLabelValue = styled.div<{ $hasPrice: boolean }>`
  ${priceLabelStyles};
  height: 58px;
  padding-top: 23px;
  padding-bottom: 17px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.coolGray.cool300};
  ${({ $hasPrice }) =>
    !$hasPrice &&
    css`
      border-bottom: none;
    `};
`

const PriceLabelBox = styled.div`
  width: 100%;
`

const minValueInSet = reduceSet<number, number | undefined>(
  undefined,
  (acc: number | undefined, price): number | undefined =>
    acc == null ? price : Math.min(acc, price),
)

const maxValueInSet = reduceSet<number, number | undefined>(
  undefined,
  (acc: number | undefined, price): number | undefined =>
    acc == null ? price : Math.max(acc, price),
)

/**
 * Display variant price when authentication state settled
 */
const ItemSelectorOptionPriceReactive: StyledFunctionComponent<{
  item: ConfigurableProductDetailsFragment
  skus: ReadonlyArray<string>
}> = ({ className, item, skus }) => {
  const productResult = useQueryProductByUrlKeyVaryByAuth(item.url_key ?? '')
  const [, product] = useResultWithLeftEffect(productResult, pluckData, constNull)
  const itemId = item.id

  // determine min and max price of variants identified by skus
  const { minPrice, maxPrice } = useMemo(() => {
    const variantsForAuth =
      itemId == null || product == null
        ? undefined
        : skus.map((sku) => findVariantInProductQuery(product, { itemId, sku }))

    const prices = variantsForAuth?.reduce((acc: Set<number>, variant) => {
      const price =
        variant?.product?.__typename === 'SimpleProduct' && 'price_tiers' in variant.product
          ? variant.product.price_tiers?.[0]?.final_price?.value ?? undefined
          : undefined
      if (price != null) {
        acc.add(price)
      }
      return acc
    }, new Set<number>())

    // NOTE: these prices will likely always be the same, but let's support a range just in case
    return {
      minPrice: prices
        ? formatAsCurrency({ amount: minValueInSet(prices) ?? 0, ignoreFalsyAmount: true })
        : undefined,
      maxPrice: prices
        ? formatAsCurrency({ amount: maxValueInSet(prices) ?? 0, ignoreFalsyAmount: true })
        : undefined,
    }
  }, [itemId, product, skus])

  return (
    <PriceLabelBox className={className}>
      {minPrice ? (
        <>
          {minPrice}
          {!!maxPrice && maxPrice !== minPrice && ` - ${maxPrice}`}
        </>
      ) : null}
    </PriceLabelBox>
  )
}

/**
 * Loading component for selector option with price
 */
const ItemSelectorOptionPriceSkeleton: React.FC = () => (
  <PriceLabelBox>
    <TypographyShimmer $width="4em" />
  </PriceLabelBox>
)

export const ItemSelectorOptionPrice: React.FC<ItemSelectorOptionPriceProps> = ({ item, skus }) => (
  <Subscribe fallback={<ItemSelectorOptionPriceSkeleton />}>
    <ItemSelectorOptionPriceReactive item={item} skus={skus} />
  </Subscribe>
)

export const ItemSelectorOptionLabel: React.FC<{ label: string }> = ({ label }) => (
  <PriceLabelValue $hasPrice={false}>{label}</PriceLabelValue>
)

export const ItemSelectorOptionPaymentTimingPrice: React.FC<{
  price: number
  priceAside?: string
}> = ({ price, priceAside }) => (
  <PriceLabelBox>
    {formatAsCurrency({ amount: price, ignoreFalsyAmount: false })}
    {priceAside ? ` ${priceAside}` : ''}
  </PriceLabelBox>
)
